/*
 * Driver Dashboard
 *
 * Mobile-first home screen for drivers:
 * - Shows the driver's assigned garbage truck (only their truck)
 * - Lists routes assigned to that truck, with today's scheduled routes highlighted
 * - "Start Route" launches the live route-following view (ActiveRouteView)
 * - If a route session is already active, reopens it automatically
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../../utils/config';
import ActiveRouteView from './ActiveRouteView';

const DriverDashboard = ({ driver, onLogout }) => {
  const [vehicle, setVehicle] = useState(null);
  const [deviceStatus, setDeviceStatus] = useState(null);
  const [routes, setRoutes] = useState([]);
  const [today, setToday] = useState('');
  const [activeSession, setActiveSession] = useState(null);
  const [activeRoute, setActiveRoute] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [starting, setStarting] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  const authHeaders = useCallback(() => ({
    headers: { 'Authorization': `Bearer ${localStorage.getItem('driverToken')}` }
  }), []);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [routesRes, sessionRes] = await Promise.all([
        axios.get(`${API}/driver/routes`, authHeaders()),
        axios.get(`${API}/driver/route-sessions/active`, authHeaders())
      ]);
      setRoutes(routesRes.data.routes || []);
      setToday(routesRes.data.today || '');

      // Vehicle can 404 if none assigned yet - handle separately
      try {
        const vehicleRes = await axios.get(`${API}/driver/vehicle`, authHeaders());
        setVehicle(vehicleRes.data.vehicle);
        setDeviceStatus(vehicleRes.data.device_status);
      } catch (vehErr) {
        setVehicle(null);
        setError(vehErr.response?.data?.detail || 'No vehicle assigned');
      }

      // Resume an active session if there is one
      const session = sessionRes.data.session;
      if (session) {
        const route = (routesRes.data.routes || []).find(r => r.id === session.route_id);
        if (route) {
          setActiveSession(session);
          setActiveRoute(route);
        } else {
          // Route was deleted/unassigned while session was active - close it
          await axios.post(`${API}/driver/route-sessions/end`, {}, authHeaders());
        }
      }
    } catch (err) {
      if (err.response?.status === 401) {
        onLogout();
        return;
      }
      setError('Failed to load your data. Pull down to retry.');
    } finally {
      setLoading(false);
    }
  }, [authHeaders, onLogout]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleStartRoute = async (route) => {
    setStarting(true);
    setError('');
    try {
      const response = await axios.post(
        `${API}/driver/route-sessions/start`,
        { route_id: route.id },
        authHeaders()
      );
      setActiveSession(response.data.session);
      setActiveRoute(response.data.route);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to start route');
    } finally {
      setStarting(false);
    }
  };

  const handleEndRoute = async () => {
    try {
      await axios.post(`${API}/driver/route-sessions/end`, {}, authHeaders());
    } catch (err) {
      // Session end failure shouldn't trap the driver in the route view
    }
    setActiveSession(null);
    setActiveRoute(null);
    loadData();
  };

  // Active route view takes over the whole screen
  if (activeSession && activeRoute && vehicle) {
    return (
      <ActiveRouteView
        driver={driver}
        vehicle={vehicle}
        route={activeRoute}
        session={activeSession}
        onEndRoute={handleEndRoute}
      />
    );
  }

  const isOnline = deviceStatus?.is_online || deviceStatus?.status === 'online';
  const todayRoutes = routes.filter(r => r.is_today);
  const otherRoutes = routes.filter(r => !r.is_today);

  const renderRouteCard = (route, isToday) => (
    <div
      key={route.id}
      className={`bg-white rounded-xl shadow p-4 border-l-4 ${isToday ? '' : 'opacity-75'}`}
      style={{ borderLeftColor: route.color || '#3B82F6' }}
    >
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 truncate">{route.name}</h3>
          {route.description && (
            <p className="text-xs text-gray-500 mt-0.5 truncate">{route.description}</p>
          )}
          <div className="flex flex-wrap gap-1 mt-1.5">
            {(route.schedule_days && route.schedule_days.length > 0
              ? ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].filter(d => route.schedule_days.includes(d))
              : ['Any day']
            ).map(d => (
              <span
                key={d}
                className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                  d === today ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-600'
                }`}
              >
                {d}
              </span>
            ))}
          </div>
        </div>
        <button
          onClick={() => handleStartRoute(route)}
          disabled={starting}
          className={`ml-3 px-4 py-3 rounded-lg font-semibold text-sm whitespace-nowrap disabled:opacity-50 ${
            isToday
              ? 'bg-green-600 text-white hover:bg-green-700'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          ▶ Start Route
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-green-700 text-white px-4 py-4 shadow">
        <div className="flex items-center justify-between max-w-lg mx-auto">
          <div>
            <h1 className="text-lg font-bold">🚛 Driver Dashboard</h1>
            <p className="text-green-100 text-sm">Hi, {driver.name}</p>
          </div>
          <button
            onClick={onLogout}
            className="px-3 py-1.5 bg-green-800 rounded-lg text-sm hover:bg-green-900"
          >
            Log Out
          </button>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-4 space-y-4">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading your truck and routes...</div>
        ) : (
          <>
            {/* My Truck */}
            <div className="bg-white rounded-xl shadow p-4">
              <h2 className="text-xs font-semibold text-gray-500 uppercase mb-2">My Truck</h2>
              {vehicle ? (
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xl font-bold text-gray-900">{vehicle.license_plate}</p>
                    <p className="text-sm text-gray-600">
                      {`${vehicle.make || ''} ${vehicle.model || ''}`.trim()}
                      {vehicle.color ? ` • ${vehicle.color}` : ''}
                    </p>
                  </div>
                  <div className="text-right">
                    <span
                      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                        isOnline ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'
                      }`}
                    >
                      <span className={`w-2 h-2 rounded-full mr-1.5 ${isOnline ? 'bg-green-500' : 'bg-gray-400'}`}></span>
                      {isOnline ? 'GPS Online' : 'GPS Offline'}
                    </span>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-yellow-700">
                  ⚠️ No truck has been assigned to you yet. Please contact your administrator.
                </p>
              )}
            </div>

            {/* Today's Routes */}
            <div>
              <h2 className="text-sm font-semibold text-gray-700 mb-2">
                📅 Today's Routes ({today})
              </h2>
              {todayRoutes.length === 0 ? (
                <div className="bg-white rounded-xl shadow p-6 text-center">
                  <div className="text-3xl mb-2">🎉</div>
                  <p className="text-gray-600 text-sm">No routes scheduled for today</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {todayRoutes.map(r => renderRouteCard(r, true))}
                </div>
              )}
            </div>

            {/* Other Routes */}
            {otherRoutes.length > 0 && (
              <div>
                <h2 className="text-sm font-semibold text-gray-500 mb-2">Other Days</h2>
                <div className="space-y-3">
                  {otherRoutes.map(r => renderRouteCard(r, false))}
                </div>
              </div>
            )}

            {routes.length === 0 && vehicle && (
              <div className="bg-white rounded-xl shadow p-6 text-center">
                <div className="text-3xl mb-2">🗺️</div>
                <p className="text-gray-600 text-sm">
                  No routes assigned to your truck yet.<br />
                  Ask your administrator to assign routes to {vehicle.license_plate}.
                </p>
              </div>
            )}

            {/* Help / instructions */}
            <div className="bg-white rounded-xl shadow overflow-hidden">
              <button
                onClick={() => setShowHelp(!showHelp)}
                className="w-full px-4 py-3 flex items-center justify-between text-sm font-semibold text-gray-700"
              >
                <span>❓ How this app works</span>
                <span className="text-gray-400">{showHelp ? '▲' : '▼'}</span>
              </button>
              {showHelp && (
                <div className="px-4 pb-4 text-sm text-gray-700 space-y-3 border-t border-gray-100 pt-3">
                  <div>
                    <p className="font-semibold">1. Your truck</p>
                    <p className="text-gray-600">You only see the truck assigned to you. "GPS Online" means the truck's tracker is reporting. If it says Offline, the map may not update — tell your supervisor.</p>
                  </div>
                  <div>
                    <p className="font-semibold">2. Today's Routes</p>
                    <p className="text-gray-600">These are the collection routes your truck must run <strong>today</strong>. Routes for other days are listed below them, greyed out.</p>
                  </div>
                  <div>
                    <p className="font-semibold">3. Press ▶ Start Route when you begin</p>
                    <p className="text-gray-600">A live map opens showing the route line, the numbered stops, and your truck's real position. Drive the route from the green start point to the red end point, following the arrows on the line.</p>
                  </div>
                  <div>
                    <p className="font-semibold">4. Stay ON ROUTE</p>
                    <p className="text-gray-600">The bar at the top is <span className="text-green-700 font-semibold">green (✅ ON ROUTE)</span> while you follow the route, and turns <span className="text-red-700 font-semibold">red (⚠️ OFF ROUTE)</span> if you drive more than 100 m away from it — get back on the route when you see red. The white progress bar shows how much of the route you have completed.</p>
                  </div>
                  <div>
                    <p className="font-semibold">5. Press ⏹ End Route when finished</p>
                    <p className="text-gray-600">End the route when you complete it (or must stop). Your start and end times are recorded for the office. If you close the app by accident, log back in — your active route reopens automatically.</p>
                  </div>
                  <p className="text-xs text-gray-500 pt-1">Questions or wrong truck/route assigned? Contact your administrator.</p>
                </div>
              )}
            </div>

            <button
              onClick={loadData}
              className="w-full py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50"
            >
              🔄 Refresh
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default DriverDashboard;
